/**
 * Flight Status page wrapper (MMT only).
 * URLs:
 * - Airline: /flights/{airline_url_phrase}/{flight_code}-flight-status.html
 * - Route: /flights/{src_url_phrase}-{dest_url_phrase}-flight-status.html
 * - Airport: /flights/{airport_url_phrase}-{city_code}-{city_url_phrase}-flight-status.html
 *   e.g. /flights/indira_gandhi_international_airport-del-new_delhi-flight-status.html
 */
import React, { useState, useMemo, useRef, useEffect } from "react";
import { transformToHtml, getCrId } from "../../utils/CommonUtils";
import { URL_ATTRIBUTE_AIRLINE } from "../../utils/Constants";
import {
  FlightStatusHeader,
  FlightStatusDetail,
  FlightStatusRouteListing,
  FlightStatusAirportListing,
  FlightStatusNoFlightsBanner,
  FLIGHT_SEARCH_TYPE_OPTIONS,
} from "../FlightStatus";
import {
  PAGE_TYPE_FLIGHT_INFORMATION_SYSTEM_AIRPORT,
  PAGE_TYPE_FLIGHT_INFORMATION_SYSTEM_ROUTE,
} from "../../utils/Constants";

function getInitialFormData(data) {
  const flightCode = data?.flight_code || "";
  const airlineDisplay = flightCode.replace(/_/g, " ").toUpperCase();
  const today = new Date().toISOString().slice(0, 10);
  const fromCode = data?.flightStatusFromCode || "";
  const toCode = data?.flightStatusToCode || "";
  const airportCode = data?.flightStatusAirportCode || fromCode;
  return {
    FROM: fromCode,
    TO: toCode,
    AIRPORT: airportCode,
    FROM_DISPLAY: data?.flightStatusFromDisplay || "",
    TO_DISPLAY: data?.flightStatusToDisplay || "",
    AIRPORT_DISPLAY: data?.flightStatusAirportDisplay || data?.flightStatusFromDisplay || "",
    AIRLINE: airlineDisplay,
    DATE: today,
    selectedOption:
      data?.defaultSearchOption || FLIGHT_SEARCH_TYPE_OPTIONS.BY_FLIGHT,
  };
}

export default function FlightStatusPage(props) {
  const { data } = props || {};
  const pageData = data?.pageData || data || {};
  const fisStatus = data?.fisStatus ?? pageData.fisStatus;
  const fisRouteListing = data?.fisRouteListing ?? pageData.fisRouteListing;
  const fisAirportListing = data?.fisAirportListing ?? pageData.fisAirportListing;
  const pageType = data?.pageType ?? pageData.pageType;
  const isRouteFisPage = pageType === PAGE_TYPE_FLIGHT_INFORMATION_SYSTEM_ROUTE;
  const isAirportFisPage = pageType === PAGE_TYPE_FLIGHT_INFORMATION_SYSTEM_AIRPORT;
  const isListingFisPage = isRouteFisPage || isAirportFisPage;
  const airlineDetails = data?.airlineDetails ?? pageData.airlineDetails;
  const urlAttributeAirline =
    data?.[URL_ATTRIBUTE_AIRLINE] ?? pageData[URL_ATTRIBUTE_AIRLINE] ?? data?.airline_url_phrase;

  const containerRef = useRef(null);
  const routeSidebarInMiddle =
    isListingFisPage && (data?.rightBlock || data?.filterBlock)
      ? `${data?.filterBlock || ""}${data?.rightBlock || ""}`
      : "";
  const middleBlockHtml = `${data?.contentTop ? data.contentTop : ""}${data?.contentMiddle ? data.contentMiddle : ""}${routeSidebarInMiddle}${data?.content ? data.content : ""}${data?.contentBottom ? data.contentBottom : ""}${data?.bottomBlock ? data.bottomBlock : ""}`;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const buttons = container.querySelectorAll(".ViewFareBtn");
    buttons?.forEach((button) => {
      const url = button.getAttribute("data-url");
      if (url) {
        button.onclick = () => window.location.replace(url);
      }
    });
    // Wire carousel for top_airline_flight_status / top_route_flight_status / top_airport_flight_status blocks (prev/next/dots)
    const fsCarouselWrappers = container.querySelectorAll(
      "[id^='topAirlineFlightStatus_'],[id^='topRouteFlightStatus_'],[id^='topAirportFlightStatus_']",
    );
    fsCarouselWrappers.forEach((wrapper) => {
      const slides = wrapper.querySelectorAll(".fsCarouselSlide");
      const dots = wrapper.querySelectorAll(".fsCarouselDot");
      const prev = wrapper.querySelector(".fsCarouselPrev");
      const next = wrapper.querySelector(".fsCarouselNext");
      const totalSlides = slides.length;
      if (totalSlides <= 1) return;

      const setButtonState = (btn, disabled) => {
        if (!btn) return;
        btn.disabled = disabled;
        btn.setAttribute("aria-disabled", String(disabled));
        btn.style.opacity = disabled ? "0.5" : "1";
        btn.style.color = disabled ? "#9aa0a6" : "#5f6368";
        btn.style.cursor = disabled ? "not-allowed" : "pointer";
      };

      const go = (s) => {
        slides.forEach((el, i) => {
          el.style.display = i === s ? "grid" : "none";
        });
        dots.forEach((el, i) => {
          el.style.background = i === s ? "#1a73e8" : "#dadce0";
        });
        setButtonState(prev, s === 0);
        setButtonState(next, s === totalSlides - 1);
        wrapper.setAttribute("data-cur", String(s));
      };

      if (prev) prev.onclick = () => { const s = Math.max(0, parseInt(wrapper.getAttribute("data-cur") || "0", 10) - 1); go(s); };
      if (next) next.onclick = () => { const s = Math.min(totalSlides - 1, parseInt(wrapper.getAttribute("data-cur") || "0", 10) + 1); go(s); };
      dots.forEach((d, i) => { d.onclick = () => go(i); });
      go(0);
    });
  }, [middleBlockHtml]);

  // Before passing, make sure the string has data-url instead of onclick
  const sanitizedHtml = middleBlockHtml.replace(/onclick="[^"]*"/, "");

  const [formData, setFormData] = useState(() => getInitialFormData(data));

  const flightStatusHeaderData = useMemo(() => {
    if (!data) return null;
    return {
      searchOptions: undefined,
      cta: { text: "Check Status" },
      meta: fisStatus?.meta ?? { maxSelectablePastDays: 7, maxSelectableFutureDays: 30 },
      defaultSearchOption:
        data?.defaultSearchOption || FLIGHT_SEARCH_TYPE_OPTIONS.BY_FLIGHT,
    };
  }, [data, fisStatus?.meta]);

  const hasAirportListing =
    Boolean(fisAirportListing?.departures?.cards?.length) ||
    Boolean(fisAirportListing?.arrivals?.cards?.length);

  const showRouteListing = isRouteFisPage && Boolean(fisRouteListing?.cards?.length);
  const showAirportListing = isAirportFisPage && hasAirportListing;
  const showNoFlightsBanner =
    isListingFisPage && !showRouteListing && !showAirportListing;

  const detailData = useMemo(() => {
    // Route/airport listing pages use FlightStatusRouteListing / FlightStatusAirportListing only.
    // Do not fall back to FlightStatusDetail (which would surface meta_description as disclaimer).
    if (isListingFisPage) {
      return null;
    }
    const title = fisStatus?.title ?? data?.title;
    const disclaimer = fisStatus?.disclaimer ?? data?.description;
    if (!title && !disclaimer && !fisStatus?.cardsData) return null;
    return {
      title: title ?? null,
      disclaimer: disclaimer ?? null,
      shareCta: fisStatus?.shareCta ?? null,
      cardsData: fisStatus?.cardsData ?? null,
    };
  }, [fisStatus, isListingFisPage, data?.title, data?.description]);

  const handleSearch = () => {
    const selectedOption =
      formData?.selectedOption || FLIGHT_SEARCH_TYPE_OPTIONS.BY_FLIGHT;
    const fromCode = String(formData?.FROM || data?.flightStatusFromCode || "").trim();
    const toCode = String(formData?.TO || data?.flightStatusToCode || "").trim();
    const airportCode = String(
      formData?.AIRPORT || data?.flightStatusAirportCode || fromCode || "",
    ).trim();
    const airlineParam = (formData.AIRLINE || "").replace(/\s+/g, "");
    const dateParam = formData.DATE || new Date().toISOString().slice(0, 10);
    const params = new URLSearchParams({
      from: fromCode,
      to: toCode,
      airport: airportCode,
      airline: airlineParam,
      date: dateParam,
      selectedOption,
      crId: getCrId(),
    });
    const baseUrl =
      selectedOption === FLIGHT_SEARCH_TYPE_OPTIONS.BY_AIRPORT
        ? "https://www.makemytrip.com/flight/flight-status"
        : "https://www.makemytrip.com/flight/flight-status/detail";
    const detailUrl = `${baseUrl}?${params.toString()}`;
    if (typeof window !== "undefined") {
      window.location.href = detailUrl;
    }
  };

  const logEvent = () => {};
  const clearRefreshTimer = () => {};

  return (
    <div className="flight-status-page-wrapper flight-status-page">
      {flightStatusHeaderData && (
        <div className="pageStickyHder flight-status-search">
          <FlightStatusHeader
            {...flightStatusHeaderData}
            onSearch={handleSearch}
            formData={formData}
            setFormData={setFormData}
          />
        </div>
      )}
      <div className="fli-intl-container prepend_top20 clearfix listingContainer">
        <div className="makeCenterCol clearfix noPadding">
          <div className="makeRelative padding10">
            <span className="bgGradient" aria-hidden />
            {showRouteListing ? (
              <div className="fsd-content-wrap fsd-content-wrap--route-listing">
                <FlightStatusRouteListing
                  listing={fisRouteListing}
                  h1Header={data?.h1Header}
                />
              </div>
            ) : null}
            {showAirportListing ? (
              <div className="fsd-content-wrap fsd-content-wrap--route-listing">
                <FlightStatusAirportListing
                  listing={fisAirportListing}
                  h1Header={data?.h1Header}
                />
              </div>
            ) : null}
            {showNoFlightsBanner ? (
              <div className="fsd-content-wrap fsd-content-wrap--route-listing">
                <FlightStatusNoFlightsBanner errorData={fisStatus?.error?.data} />
              </div>
            ) : null}
            {detailData ? (
              <div className="fsd-content-wrap">
                <FlightStatusDetail
                  {...detailData}
                  logEvent={logEvent}
                  clearRefreshTimer={clearRefreshTimer}
                  h1Header={data?.h1Header}
                />
              </div>
            ) : null}
          </div>
          <div
            id="seoContentMiddle"
            className="fsd-seo-content-middle seoOnlyBlock">
            <div ref={containerRef}>{transformToHtml(sanitizedHtml)}</div>
          </div>
          {!isListingFisPage ? (
            <div
              id="seoRightBlock"
              className="seoOnlyBlock col-lg-3 col-md-12 col-sm-12 col-xs-12 airline-right-container nopadding-mob paddR0 ">
              {data?.filterBlock && transformToHtml(data?.filterBlock)}
              {data?.rightBlock && transformToHtml(data?.rightBlock)}
              {data?.leftBlock && <div dangerouslySetInnerHTML={{ __html: data?.leftBlock }} />}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
